import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizationsApi } from '../api/organizations.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import DeleteOrganizationModal from '../components/DeleteOrganizationModal';

const LOGO_COLORS = [
  'linear-gradient(135deg,#1D4ED8,#3B82F6)',
  'linear-gradient(135deg,#7E22CE,#A855F7)',
  'linear-gradient(135deg,#C2410C,#F97316)',
  'linear-gradient(135deg,#15803D,#22C55E)',
  'linear-gradient(135deg,#B45309,#F59E0B)',
  'linear-gradient(135deg,#065F46,#10B981)',
  'linear-gradient(135deg,#BE123C,#F43F5E)',
  'linear-gradient(135deg,#0369A1,#0EA5E9)',
];

const TYPE_CLS = {
  Employer: 'org-type-employer',
  University: 'org-type-university',
  Church: 'org-type-church',
  Partner: 'org-type-partner',
  Nonprofit: 'org-type-nonprofit',
  Healthcare: 'org-type-healthcare',
  Media: 'org-type-media',
  Sponsor: 'org-type-employer',
  Referral: 'org-type-partner',
};
const PLAN_CLS = {
  Sponsored: 'org-plan-sponsored',
  Mixed: 'org-plan-mixed',
  Freemium: 'org-plan-freemium',
  Pilot: 'org-plan-pilot',
  Promo: 'org-plan-promo',
  Enterprise: 'org-plan-enterprise',
};
const STATUS_CLS = {
  Active: 'org-status-active',
  Pending: 'org-status-pending',
  Inactive: 'org-status-inactive',
  Suspended: 'org-status-suspended',
};

function formatSetupStatus(value) {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const SETUP_STATUS_CLS = {
  draft: 'org-status-pending',
  pending_billing: 'org-status-pending',
  pending_review: 'org-plan-promo',
  active: 'org-status-active',
};

function initials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export default function Organizations() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSetupStatus, setFilterSetupStatus] = useState('');
  const [filterState, setFilterState] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteTargetOrg, setDeleteTargetOrg] = useState(null);
  const [toast, setToast] = useState(null);
  const {
    page,
    limit,
    total,
    loading,
    setLoading,
    resetPage,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  async function loadOrgs(pageNum = page) {
    setLoading(true);
    const params = { page: pageNum, limit };
    if (filterType) params.orgType = filterType.toLowerCase();
    if (filterStatus) params.status = filterStatus.toLowerCase();
    if (filterSetupStatus) params.setupStatus = filterSetupStatus;
    if (filterState) params.state = filterState;
    if (search.trim()) params.search = search.trim();

    const result = await apiHandler(() => getOrganizationsApi(params));
    if (result.success) {
      const data = unwrapData(result);
      setOrgs(data?.organizations || []);
      applyPaginationMeta(data);
    } else {
      showToast(result.message || 'Failed to load organizations', 'error');
    }
    setLoading(false);
  }

  useEffect(() => {
    loadOrgs(page);
  }, [page]);

  function applyFilters() {
    resetPage();
    loadOrgs(1);
  }

  function resetFilters() {
    setFilterType('');
    setFilterStatus('');
    setFilterSetupStatus('');
    setFilterState('');
    setSearch('');
    resetPage();
    setTimeout(() => loadOrgs(1), 0);
    showToast('Filters cleared', 'info');
  }

  const selected = orgs.find((o) => o.id === selectedId);

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 70,
            right: 20,
            zIndex: 9999,
            background: toast.type === 'success' ? '#F0FDF4' : '#EFF6FF',
            border: `1px solid ${toast.type === 'success' ? '#b7e5bb' : '#BFDBFE'}`,
            color: toast.type === 'success' ? '#15803D' : '#1D4ED8',
            padding: '10px 18px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
          }}
        >
          {toast.msg}
        </div>
      )}

      <PageHeader title="Organizations" icon="fa-building" trail={[{ label: 'Organizations' }]} />

      <div className="fade-up delay-1">
        <div className="section-card">
          <div className="cm-toolbar">
            <div className="cm-toolbar-start">
              {/* Type Filter */}
              <select
                className="cm-filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                aria-label="Type"
              >
                <option value="">Any type</option>
                {['Sponsor', 'Referral', 'Employer', 'University', 'Church', 'Partner', 'Nonprofit', 'Healthcare', 'Media'].map(
                  (t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  )
                )}
              </select>

              {/* Status Filter */}
              <select
                className="cm-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                aria-label="Status"
              >
                <option value="">Any status</option>
                {['Active', 'Pending', 'Inactive', 'Suspended'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Setup Status Filter */}
              <select
                className="cm-filter-select"
                value={filterSetupStatus}
                onChange={(e) => setFilterSetupStatus(e.target.value)}
                aria-label="Setup Status"
              >
                <option value="">Any setup status</option>
                {['draft', 'pending_billing', 'pending_review', 'active'].map((s) => (
                  <option key={s} value={s}>
                    {formatSetupStatus(s)}
                  </option>
                ))}
              </select>

              {/* State Filter */}
              <select
                className="cm-filter-select"
                value={filterState}
                onChange={(e) => setFilterState(e.target.value)}
                aria-label="State"
              >
                <option value="">Any state</option>
                {['WI', 'TX', 'AZ', 'FL', 'CA', 'NY', 'IL'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              {/* Search Bar */}
              <div className="cm-search-wrap">
                <i className="fas fa-magnifying-glass" />
                <input
                  type="text"
                  className="cm-search-input"
                  placeholder="Search name, email, or mob"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>

              {/* Apply Button */}
              <button
                type="button"
                className="action-btn btn-green"
                onClick={applyFilters}
                style={{ height: 36, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 6, borderRadius: 9 }}
              >
                <i className="fas fa-check" />
                Apply
              </button>

              {/* Reset link */}
              <span
                className="cm-reset-link"
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontSize: 13, color: '#64748B', marginLeft: 8, textDecoration: 'underline' }}
                onClick={resetFilters}
              >
                Reset filters
              </span>
            </div>
          </div>

          <div className="org-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 960 }}>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Type</th>
                  <th>Users</th>
                  <th>Plan</th>
                  <th>Setup</th>
                  <th>State</th>
                  <th>Status</th>
                  <th style={{ width: 100, textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>Loading…</td>
                  </tr>
                ) : orgs.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>No organizations match your filters.</td>
                  </tr>
                ) : (
                  orgs.map((o, i) => (
                    <tr
                      key={o.id}
                      className={`org-row${selectedId === o.id ? ' org-row-selected' : ''}`}
                      onClick={() => setSelectedId(selectedId === o.id ? null : o.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="org-name-cell" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="org-logo"
                            style={{
                              background: LOGO_COLORS[i % LOGO_COLORS.length],
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 700
                            }}
                          >
                            {initials(o.organization || 'OR')}
                          </div>
                          <div>
                            <div className="org-name-text" style={{ fontWeight: 600, color: '#0F172A' }}>{o.organization}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`org-type ${TYPE_CLS[o.type] || ''}`}>{o.type}</span>
                      </td>
                      <td>
                        <span className="org-users">{(o.users || 0).toLocaleString()}</span>
                      </td>
                      <td>
                        <span className={`org-plan ${PLAN_CLS[o.plan] || ''}`}>{o.plan}</span>
                      </td>
                      <td>
                        <span className={`org-status ${SETUP_STATUS_CLS[o.setupStatus] || ''}`}>
                          {formatSetupStatus(o.setupStatus)}
                        </span>
                      </td>
                      <td>
                        <span className="org-state">{o.state || '—'}</span>
                      </td>
                      <td>
                        <span className={`org-status ${STATUS_CLS[o.status] || ''}`}>{o.status}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {o.setupStatus === 'pending_review' && (
                            <button
                              type="button"
                              className="action-btn"
                              style={{ padding: '6px 10px', height: 'auto', background: '#FEF3C7', color: '#B45309', border: '1px solid #FCD34D' }}
                              title="Review setup"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/organizations/${o.id}?tab=sponsor`);
                              }}
                            >
                              <i className="fas fa-clipboard-check" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="action-btn btn-blue"
                            style={{ padding: '6px 10px', height: 'auto' }}
                            title="View detail"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/organizations/${o.id}`);
                            }}
                          >
                            <i className="fas fa-eye" />
                          </button>
                          <button
                            type="button"
                            className="action-btn"
                            style={{ padding: '6px 10px', height: 'auto', background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}
                            title="Delete organization"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTargetOrg(o);
                            }}
                          >
                            <i className="fas fa-trash-can" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          {...paginationProps}
          actions={
            <button
              type="button"
              className="action-btn btn-blue"
              onClick={() =>
                selectedId
                  ? navigate(`/organizations/${selectedId}`)
                  : showToast('Select an organization first', 'info')
              }
            >
              <i className="fas fa-arrow-up-right-from-square" />
              Open Detail
            </button>
          }
        />
      </div>

      {deleteTargetOrg && (
        <DeleteOrganizationModal
          isOpen={!!deleteTargetOrg}
          onClose={() => setDeleteTargetOrg(null)}
          orgId={deleteTargetOrg.id}
          orgName={deleteTargetOrg.organization || deleteTargetOrg.name || 'Organization'}
          hasRoster={(deleteTargetOrg.users || deleteTargetOrg.rosterCount || 0) > 0}
          onNavigateRoster={() => navigate(`/organizations/${deleteTargetOrg.id}?tab=roster`)}
          onDeleted={() => {
            setDeleteTargetOrg(null);
            loadOrgs(page);
            showToast('Organization deleted successfully', 'success');
          }}
        />
      )}
    </>
  );
}
